export const TCO_EXPLANATION = [
  {
    title: 'Determine the Total Cost of Ownership',
  },
  {
    text: `The Edmunds True Cost to Own® (TCO®) calculator is a tool that looks at the 5-year costs of owning a vehicle, including some you might not have considered. These extra costs include depreciation, interest on your loan, taxes and fees, insurance premiums, fuel costs, maintenance and repairs. Search here to view the TCO® of any vehicle.`,
  },
  {
    title: `True Cost To Own®`,
  },
  {
    text: `Edmunds True Cost to Own® (TCO®) is proprietary data that helps you estimate the total 5-year cost of buying and owning a vehicle &mdash; including some items you may not have taken into consideration. A benefit of using our TCO® calculator is that you can easily compare the 5-year totals for different vehicles and make a more informed choice.`,
    hasHTMLContent: true,
  },
  {
    text: `The components of this TCO® calculator are car depreciation, interest on financing, taxes and fees, insurance premiums, fuel, maintenance, repairs, and any federal tax credit that may be available. In order to estimate certain mileage-dependent costs, we assume that vehicles will be driven 15,000 miles per year. For a used car, we calculate the years the vehicle has been driven using the nominal difference between the current calendar year and the vehicle's model year, and we assume that it was driven 15,000 miles during each of those years.`,
  },
  {
    text: `Note that TCO® is a comparative tool, not a predictive tool &mdash; your actual 5-year cost of owning a particular vehicle will vary depending on your personal circumstances such as your driving history and the number of miles you drive.`,
    hasHTMLContent: true,
  },
  {
    subtitle: 'How We Calculate True Cost to Own®',
  },
  {
    text: 'The TCO® calculations use the following set of assumptions: ',
  },
  {
    list: [
      'Operating costs are estimated for a 5-year period',
      'You will drive 15,000 miles per year',
      'You are financing the vehicle using traditional financing, not leasing',
      'You have an above-average credit rating for the purpose of determining your finance rate',
      'You are making a 10% down payment on the vehicle at purchase',
      'Your loan term is 60 months',
    ],
  },
  {
    text: `Using proprietary formulas, we calculate the 5-year costs for the seven cost categories that make up the TCO® (car depreciation, insurance, financing, taxes & fees, fuel, maintenance and repairs). We also take into account any applicable federal tax credit.`,
  },
  {
    subtitle: 'Explanation of True Cost to Own® Terms',
  },
  {
    paragraphTitle: 'Total Cash Price',
  },
  {
    text: `For new vehicles, the Total Cash Price displayed is the vehicle's market value price plus typically equipped options, destination charge, base tax and fees assessed by your state, and, if applicable, gas guzzler tax, less any widely available manufacturer-to-customer cash rebates. (However, we do not account for other types of cash rebates or incentives because of the variability of those offers and their eligibility requirements.) For used cars, the Total Cash Price shown is the sum of the vehicle's private-party value price in "clean" condition plus typically equipped options and base tax and fees assessed by your state.`,
  },
  {
    paragraphTitle: 'Depreciation',
  },
  {
    text: `This is the amount by which the value of a vehicle declines from its purchase price to its estimated resale value. The purchase price employed is the vehicle's Total Cash Price minus any taxes and fees included in that amount. We estimate the resale value assuming the vehicle will be in "clean" condition, will be driven 15,000 miles per year, and will be sold to a private party.`,
  },
  {
    paragraphTitle: 'Insurance',
  },
  {
    text: `This is the estimated average annual insurance premium in your state. The premium has been determined based on annual premium data for defined driver profiles and coverages (liability, comprehensive and collision) from a major national insurer. While this information is specific to vehicle make, model, model year and body type, your personal information is not taken into consideration and could greatly alter the actual premium quoted by an insurer. Factors that will affect your rate include your age, marital status, credit history, driving record, and the garaging address of your vehicle.`,
  },
  {
    paragraphTitle: 'Financing',
  },
  {
    text: `This is the interest expense on a loan in the amount of the Total Cash Price, assuming a 10% down payment and a loan term of 60 months. The interest rate used is the prevailing rate that banks and other direct automotive lenders are currently charging consumers in your geographic region who have above-average credit scores.`,
  },
  {
    text: `Note: Even if you do not finance your vehicle, the inclusion of financing cost in determining True Cost to Own® is still appropriate because it reflects the estimated "opportunity cost" (i.e., the amount you may earn) if you were to invest the Purchase Price instead of using it to purchase the vehicle.`,
  },
  {
    paragraphTitle: 'Taxes & Fees',
  },
  {
    text: `This consists of the base sales (or use) taxes, license and registration fees in your state, and gas-guzzler tax if applicable. These taxes and fees are often based on a percentage of the purchase price and generally decrease as the vehicle ages and loses its value.`,
  },
  {
    text: `Note: The state sales/use tax rate that we use includes the average local and county taxes assessed in that state.`,
  },
  {
    paragraphTitle: 'Fuel',
  },
  {
    text: `This expense is based on the revised EPA mileage ratings, assuming consumption consists of 45% highway and 55% city driving and that the vehicle is equipped with the transmission that is standard equipment for that vehicle. Cost estimates are based on the current one-year moving average of self-service prices in your state, using regular unleaded gasoline for vehicles whose manufacturers require regular; premium unleaded gasoline for vehicles whose manufacturers recommend or require premium; or diesel fuel for diesel vehicles.`,
  },
  {
    paragraphTitle: 'Maintenance',
  },
  {
    text: `This is the estimated expense of the two types of maintenance: scheduled and unscheduled. Scheduled maintenance is the performance of factory-recommended items at periodic mileage or calendar intervals. Unscheduled maintenance includes wheel alignment and the replacement of items such as the battery, brakes, headlights, hoses, exhaust system parts, taillight/turn signal bulbs, tires and wiper blades/inserts. Estimated tire replacement costs are supplied to Edmunds by The Tire Rack Inc.`,
  },
  {
    paragraphTitle: 'Repairs',
  },
  {
    text: `This is the estimated expense for repairs not covered by the vehicle manufacturer's warranties over the five years from the date of purchase, assuming 15,000 miles are driven annually. We estimate this expense based on the cost of a typical "zero deductible" extended warranty for the vehicle, minus the estimated amount of that cost that consists of the warranty provider's overhead and profit.`,
  },
  {
    paragraphTitle: 'Federal Tax Credits',
  },
  {
    text: `This is the tax credit that is provided for under the Energy Policy Act of 2005. A tax credit is subtracted directly from the total amount of federal tax you owe. The tax credit is for electric fuel vehicles.`,
  },
  {
    text: `The credit is only available to the original purchaser of a new qualifying vehicle and is subject to certain phaseout rules that we take into consideration when computing TCO®. If a qualifying vehicle is leased to a consumer, the leasing company may claim the credit.`,
  },
];

export const HOW_TO_DETERMINE_TCO = [
  {
    title: 'How to Determine the True Cost of Car Ownership',
  },
  {
    text: `You've narrowed your choices to three new vehicles, all highly rated luxury compact SUVs with roughly comparable prices. But how will you know which will cost you more to operate in the long run?`,
  },
  {
    text: `Anyone faced with a similar choice &mdash; in fact, anyone who is shopping for a new car &mdash; should use the Edmunds True Cost to Own® and Car Depreciation Calculator, which reveals car ownership costs over a 5-year period. When you're car shopping, you tend to pay a lot of attention to vehicle features, the purchase price and warranty, but perhaps you aren't as attentive to what the day-to-day costs of the car will be once it's in your garage.`,
    hasHTMLContent: true,
  },
  {
    text: `But even cars that cost the same can have different depreciation, fuel costs, maintenance, insurance and repair expenses. This Edmunds car ownership calculator can be a valuable research tool when you're comparing vehicles, whether they're of the same type (two sedans or two SUVs, for example) or of very different types. In addition to using this tool on Edmunds mobile and desktop sites, you can find ownership cost information on the Edmunds car shopping app for iOS and Android devices.`,
  },
  {
    text: `For the most powerful application of this car ownership information, look for it in the Compare Cars feature on Edmunds. Select two or more vehicles and then click "Ownership Costs" on the top navigation, or just scroll down. You can easily scan across the screen and see the ownership costs side by side. Additionally, the chart lists an average cost-per-mile figure.`,
  },
  {
    text: `You can use this tool to help you decide between very different vehicles. Let's say you want an electric sedan &mdash; maybe a Tesla Model 3. Your significant other wants a more practical sedan &mdash; maybe a Nissan Leaf. You can dive into the comparative ownership costs of both vehicles for more clarity on the decision.`,
    hasHTMLContent: true,
  },
  {
    subtitle: 'Cost of Owning a Car',
  },
  {
    text: `Edmunds True Cost to Own takes eight car ownership costs into account:`,
  },
  {
    list: [
      '<span class="fw-bold">Car depreciation:</span> how much value the car loses each year',
      '<span class="fw-bold">Interest on financing:</span> the amount of interest paid over five years',
      '<span class="fw-bold">Taxes and fees:</span> the total of all sales tax, fees and registry costs each year',
      '<span class="fw-bold">Insurance premiums:</span> the average cost of insuring the car',
      '<span class="fw-bold">Fuel:</span> how much you have to pay for the type of fuel that the car requires &mdash; regular or premium gasoline or diesel fuel',
      `<span class="fw-bold">Maintenance:</span> the total cost of performing all the scheduled maintenance found in the vehicle's owner's manual`,
      '<span class="fw-bold">Repairs:</span> the projected cost of fixing common mechanical problems for this vehicle',
      '<span class="fw-bold">Federal tax credit:</span> the amount of any tax credit available for alternative fuel vehicles',
    ],
    hasHTMLContent: true,
  },
  {
    text: `The TCO page for each vehicle breaks information down into the following two sections: TCO Summary and 5-Year Details.`,
  },
  {
    subtitle: '1. Ownership Costs Summary Section',
  },
  {
    text: 'The summary shows the results of the TCO calculations. It gives you two figures:',
  },
  {
    list: [
      'The True Cost to Own figure. This is all of the ownership and operation costs for five years.',
      `The Total Cash Price for new cars is the sum of a vehicle's market value, along with typically equipped options, destination charge, base tax for the state, and any applicable luxury or gas-guzzler taxes. For used cars, the total cash price shown is the sum of the vehicle's private-party value in "clean" condition, plus typically equipped options and base tax and fees in your state.`,
    ],
  },
  {
    subtitle: '2. 5-Year Breakdown',
  },
  {
    text: `This section gives a breakdown of how the vehicle's expenses change over the 5-year period. It shows the car's depreciation, meaning its decline in value. This information would be important if, for example, there were a sudden drop-off in value after the third year. Knowing this, the owner could sell the car before the cliff and avoid the subsequent loss of its value.`,
  },
  {
    text: `The other parts of the breakdown show the typical expenses related to the purchase of this vehicle, including such items as fuel, insurance, maintenance costs and repairs. These are costs that people sometimes overlook when they're buying a car. Seeing them listed and totaled can help you plan for this large purchase.`,
  },
  {
    text: `By looking at Edmunds car ownership data, you get a snapshot of the car's costs over time. You will see the big picture, not just today's price tag.`,
  },
];

export const FAQ_CONTENT = [
  {
    question: 'What is total cost of ownership?',
    answer: `Total cost of ownership, or TCO, is the purchase price of an asset plus its costs of operation, according to Investopedia. It's a more holistic way of looking at what a product actually costs you. When you're choosing among things you might buy &mdash; such as a vehicle &mdash; you look not just at the price (the short-term cost) but also the cost to operate it (the long-term cost). In the final analysis, the asset with lower total cost of ownership is the better value. Edmunds applies this concept to car shopping with its True Cost to Own (TCO) pricing system. It essentially is a long-term car cost calculator that does all the work for you.`,
  },
  {
    question: 'How much does a vehicle depreciate in a year?',
    answer: `Different vehicles depreciate at different rates. In general, new cars lose 23.5% of their manufacturer's suggested retail price (MSRP) after a year and about 60% of their MSRP in the first five years. The percentage of depreciation tapers off in subsequent years.`,
  },
  {
    question: 'How much does it cost to own a new car after one year?',
    answer: `<p>One factor in the total cost of ownership that people overlook is depreciation, so let's start there. Edmunds data shows a range of depreciation for vehicles between 6% and 45% of MSRP after the first year of ownership. On average, though, new cars lose 23.5% of their MSRP after a year. In addition to each vehicle's depreciation, these components factor into the annual cost of ownership:</p>
      <ul>
        <li>Taxes and fees</li>
        <li>Fuel</li>
        <li>Maintenance</li>
        <li>Repairs</li>
        <li>Federal tax credit, if applicable</li>
        <li>Financing costs (interest)</li>
        <li>Insurance premiums</li>
      </ul>
    `,
  },
  {
    question: 'How do you calculate car depreciation?',
    answer: `<p>A general car depreciation calculator works from a car's initial value and shows a dollar amount and percentage of depreciation over time. You'll easily find many such tools online.</p>
    <p>Edmunds, however, uses a more sophisticated approach in determining depreciation. Edmunds analysts calculate total depreciation by taking the new car's current market value and subtracting the estimated yearly future resale value of the vehicle. Edmunds bases its estimated future resale values on these input factors:</p>
      <ul>
        <li>Recent auction and retail transaction data of current and older model years</li>
        <li>Supply and demand</li>
        <li>New vehicle pricing and incentives</li>
        <li>New vehicle redesign or improvement info</li>
        <li>Vehicle competitive segment</li>
        <li>Forecast of the economy</li>
      </ul>
    `,
  },
  {
    question: 'Can I use my car as a tax write-off?',
    answer: `<p>There's no better source for this answer than the Internal Revenue Service: "If you use your car in your job or business and you use it only for that purpose, you may deduct its entire cost of operation." There are limits, of course, and the IRS describes those in detail online in Tax Topics under "Business Use of Car." The IRS adds that if you "use the car for both business and personal purposes, you may deduct only the cost of its business use."</p>
    <p>The IRS says that you can "generally figure the amount of your deductible car expense by using one of two methods: the standard mileage rate method or the actual expense method."</p>
    <p>"If you qualify to use both methods, you may want to figure your deduction both ways before choosing a method to see which one gives you a larger deduction." More details are available in IRS Publication 463, "Travel, Gift, and Car Expenses."</p>`,
  },
];
